import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";

type AuditLog = components["schemas"]["ExpenseAuditLogResponse"];

// Unpaged: an expense collects a handful of entries, not hundreds.
export function ExpenseAuditLogTable({ expenseId }: { expenseId: string }) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchLogs() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/expenses/{id}/audit-logs", {
        params: { path: { id: expenseId } },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load the expense's history.");
      } else {
        setLogs(data ?? []);
      }
      setLoading(false);
    }

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [expenseId]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>To state</TableHead>
            <TableHead>Detail</TableHead>
            <TableHead>Actor</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                <Spinner className="mx-auto size-5" />
              </TableCell>
            </TableRow>
          ) : logs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground h-24 text-center">
                No history recorded.
              </TableCell>
            </TableRow>
          ) : (
            logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell className="whitespace-nowrap text-sm">{formatDateTime(log.createdAt)}</TableCell>
                <TableCell className="font-mono text-xs">{log.event}</TableCell>
                <TableCell>
                  {log.toState ? (
                    <Badge variant="outline">{log.toState}</Badge>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="max-w-xl whitespace-normal text-sm">
                  {log.detail || <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell>
                  {log.actorUserId ? (
                    <Link to={`/users/${log.actorUserId}`}>
                      <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                        {log.actorUserId.slice(0, 8)}...
                      </Badge>
                    </Link>
                  ) : (
                    <span className="text-sm text-muted-foreground">System</span>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
